import React, { Component, ErrorInfo, useEffect } from "react";

import { logError } from "@/app/_/config/logError";
import { State } from "@/state";
import { DEFAULT_OPEN_THREAD_TABS } from "@/state/threadTabs";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// This is the actual error boundary that uses class component lifecycle methods
class ErrorBoundaryClass extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // React's error boundary "handles" the throw - the original
    // exception never reaches window.error - so it is reported here,
    // with the React component stack beside it.
    logError(error, {
      source: "react.error_boundary",
      component_stack: errorInfo.componentStack ?? "",
      page_path: window.location.pathname,
    });

    // Reset global store
    State.setState({
      activeAppView: "thread",
      activeThreadNodeId: null,
      openThreadTabs: DEFAULT_OPEN_THREAD_TABS,
      // activeEntityIdentifiers: [],
      activePanelNodeIds: {},
      activePanelNodeId: null,
      activeTab: "focus",
      isMutating: null,
      isNodePanelOpen: false,
      isThreadPanelOpen: false,
      isPanelResizing: false,
      isSidebarMinimized: false,
      isSidebarResizing: false,
      onSetActiveTab: null,
      previousAppView: null,
      queuedPanelNodeIds: [],
      reviewableDialogNodeIds: [],
      scopedEntityIdentifier: null,
      snackbarMessage:
        "An error occurred and the application state has been reset.",
      userIdentifier: null,
    });
  }

  resetErrorBoundary = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div className="p-8 flex flex-col items-center justify-center h-screen">
          <Typography level="h3" className="mb-4">
            Something went wrong
          </Typography>
          <Typography className="mb-8">
            The application encountered an error. The state has been reset.
          </Typography>
          <Button onClick={this.resetErrorBoundary}>Retry</Button>
        </div>
      );
    }

    return this.props.children;
  }
}

// This wrapper component handles global uncaught errors
export function ErrorBoundary({ children }: ErrorBoundaryProps) {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      // ResizeObserver "loop completed" notifications are a known
      // benign warning from scroll components and a state
      // reset on every layout reflow would be way too aggressive.
      if (
        event.message &&
        typeof event.message === "string" &&
        event.message.includes(
          "ResizeObserver loop completed with undelivered notifications",
        )
      ) {
        return;
      }

      // Said with where it came from, so a state reset is told
      // apart from an error caught anywhere else.
      logError(event.error ?? new Error(event.message), {
        source: "window.error",
        reset_state: true,
      });

      // Reset global store
      State.setState({
        activeAppView: "thread",
        activeThreadNodeId: null,
        openThreadTabs: DEFAULT_OPEN_THREAD_TABS,
        activeEntityIdentifiers: [],
        activePanelNodeIds: {},
        activePanelNodeId: null,
        activeTab: "focus",
        isMutating: null,
        isNodePanelOpen: false,
        isThreadPanelOpen: false,
        isPanelResizing: false,
        isSidebarMinimized: false,
        isSidebarResizing: false,
        onSetActiveTab: null,
        previousAppView: null,
        queuedPanelNodeIds: [],
        reviewableDialogNodeIds: [],
        scopedEntityIdentifier: null,
        snackbarMessage:
          "An error occurred and the application state has been reset.",
        userIdentifier: null,
      });
    };

    window.addEventListener("error", handleError);

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Deliberately no state reset here - a rejected promise rarely
      // means the UI is corrupted (Apollo timeouts, aborted fetches,
      // ...) and resetting on every transient failure would be
      // unbearable.
      const reason =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));
      logError(reason, { source: "window.unhandledrejection" });
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
    };
  }, []);

  // Use the class error boundary to catch errors in React component tree
  return <ErrorBoundaryClass>{children}</ErrorBoundaryClass>;
}
